import { Users, Building2, Eye, FileText, Briefcase, TrendingUp, UserPlus, PlusCircle, Heart, MessageCircle, ThumbsUp, Database, Code, CheckCircle, Award, Smartphone, Crown, Sparkles, UserCheck, DollarSign, Repeat } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

interface MetricsCardsProps {
  // Métricas globais
  totalUsers: number;
  totalCompanies: number;
  totalActiveJobs: number;
  totalJobViews: number;
  
  // Métricas do período
  newUsersInPeriod: number;
  newCompaniesInPeriod: number;
  postsInPeriod: number;
  jobViewsInPeriod: number;
  likesInPeriod: number;
  commentsInPeriod: number;
  commentLikesInPeriod: number;
  datasetClicksInPeriod: number;
  challengeAttemptsInPeriod: number;
  correctChallengesInPeriod: number;
  certificatesIssuedInPeriod: number;
  pwaInstallsTotal?: number;
  pwaInstallsInPeriod?: number;

  // Assinaturas de candidatos
  candidateSubsActive?: number;
  candidateTrialsActive?: number;
  candidatePremiumMonthly?: number;
  candidatePremiumYearly?: number;
  candidateMrr?: number;
  candidateNewPremiumInPeriod?: number;
  candidateNewTrialsInPeriod?: number;
  candidateConversionsInPeriod?: number;
}

export function MetricsCards({
  totalUsers,
  totalCompanies,
  totalActiveJobs,
  totalJobViews,
  newUsersInPeriod,
  newCompaniesInPeriod,
  postsInPeriod,
  jobViewsInPeriod,
  likesInPeriod,
  commentsInPeriod,
  commentLikesInPeriod,
  datasetClicksInPeriod,
  challengeAttemptsInPeriod,
  correctChallengesInPeriod,
  certificatesIssuedInPeriod,
  pwaInstallsTotal = 0,
  pwaInstallsInPeriod = 0,
  candidateSubsActive = 0,
  candidateTrialsActive = 0,
  candidatePremiumMonthly = 0,
  candidatePremiumYearly = 0,
  candidateMrr = 0,
  candidateNewPremiumInPeriod = 0,
  candidateNewTrialsInPeriod = 0,
  candidateConversionsInPeriod = 0,
}: MetricsCardsProps) {
  const globalMetrics = [
    {
      title: "Total de Candidatos",
      value: totalUsers,
      icon: Users,
      description: "Acumulado",
      isGlobal: true
    },
    {
      title: "Total de Empresas",
      value: totalCompanies,
      icon: Building2,
      description: "Acumulado",
      isGlobal: true
    },
    {
      title: "Vagas Ativas",
      value: totalActiveJobs,
      icon: Briefcase,
      description: "Snapshot atual",
      isGlobal: true
    },
    {
      title: "Instalações do App (PWA)",
      value: pwaInstallsTotal,
      icon: Smartphone,
      description: "Acumulado",
      isGlobal: true
    },
  ];

  const periodMetrics = [
    {
      title: "Novos Candidatos",
      value: newUsersInPeriod,
      icon: UserPlus,
      description: "No período",
      isGlobal: false
    },
    {
      title: "Novas Empresas",
      value: newCompaniesInPeriod,
      icon: PlusCircle,
      description: "No período",
      isGlobal: false
    },
    {
      title: "Posts Criados",
      value: postsInPeriod,
      icon: FileText,
      description: "No período",
      isGlobal: false
    },
    {
      title: "Views de Vagas",
      value: jobViewsInPeriod,
      icon: Eye,
      description: "No período",
      isGlobal: false
    },
    {
      title: "Curtidas em Posts",
      value: likesInPeriod,
      icon: Heart,
      description: "No período",
      isGlobal: false
    },
    {
      title: "Comentários",
      value: commentsInPeriod,
      icon: MessageCircle,
      description: "No período",
      isGlobal: false
    },
    {
      title: "Curtidas em Comentários",
      value: commentLikesInPeriod,
      icon: ThumbsUp,
      description: "No período",
      isGlobal: false
    },
    {
      title: "Cliques em Datasets",
      value: datasetClicksInPeriod,
      icon: Database,
      description: "No período",
      isGlobal: false
    },
    {
      title: "Tentativas de Desafios",
      value: challengeAttemptsInPeriod,
      icon: Code,
      description: "No período",
      isGlobal: false
    },
    {
      title: "Desafios Corretos",
      value: correctChallengesInPeriod,
      icon: CheckCircle,
      description: "No período",
      isGlobal: false
    },
    {
      title: "Certificados Emitidos",
      value: certificatesIssuedInPeriod,
      icon: Award,
      description: "No período",
      isGlobal: false
    },
    {
      title: "Instalações do App",
      value: pwaInstallsInPeriod,
      icon: Smartphone,
      description: "No período",
      isGlobal: false
    },
  ];

  const renderCard = (metric: typeof globalMetrics[0] | typeof periodMetrics[0]) => {
    const Icon = metric.icon;
    return (
      <Card key={metric.title} className="relative overflow-hidden">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">
            {metric.title}
          </CardTitle>
          <Icon className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{metric.value.toLocaleString('pt-BR')}</div>
          <div className="flex items-center gap-2 mt-1">
            <Badge 
              variant={metric.isGlobal ? "secondary" : "default"} 
              className="text-[10px] px-1.5 py-0"
            >
              {metric.isGlobal ? "Global" : "Período"}
            </Badge>
            <p className="text-xs text-muted-foreground">
              {metric.description}
            </p>
          </div>
        </CardContent>
      </Card>
    );
  };

  const candidateGlobalMetrics = [
    { title: "Candidatos Assinantes Ativos", value: candidateSubsActive, icon: UserCheck, description: "Snapshot atual", isGlobal: true },
    { title: "Trials Ativos", value: candidateTrialsActive, icon: Sparkles, description: "Snapshot atual", isGlobal: true },
    { title: "Premium Mensal", value: candidatePremiumMonthly, icon: Crown, description: "Snapshot atual", isGlobal: true },
    { title: "Premium Anual", value: candidatePremiumYearly, icon: Crown, description: "Snapshot atual", isGlobal: true },
  ];

  const candidatePeriodMetrics = [
    { title: "Novos Premium", value: candidateNewPremiumInPeriod, icon: Crown, description: "No período", isGlobal: false },
    { title: "Novos Trials", value: candidateNewTrialsInPeriod, icon: Sparkles, description: "No período", isGlobal: false },
    { title: "Conversões Trial → Premium", value: candidateConversionsInPeriod, icon: Repeat, description: "No período", isGlobal: false },
  ];

  return (
    <div className="space-y-4">
      {/* Métricas Globais */}
      <div>
        <h3 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
          <TrendingUp className="h-4 w-4" />
          Métricas Acumuladas
        </h3>
        <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
          {globalMetrics.map(renderCard)}
        </div>
      </div>
      
      {/* Métricas do Período */}
      <div>
        <h3 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
          <Eye className="h-4 w-4" />
          Métricas do Período Selecionado
        </h3>
        <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
          {periodMetrics.map(renderCard)}
        </div>
      </div>

      {/* Assinaturas de Candidatos */}
      <div>
        <h3 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
          <Crown className="h-4 w-4" />
          Assinaturas de Candidatos
        </h3>
        <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
          {candidateGlobalMetrics.map(renderCard)}
          <Card className="relative overflow-hidden">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">MRR de Candidatos</CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {candidateMrr.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
              </div>
              <div className="flex items-center gap-2 mt-1">
                <Badge variant="secondary" className="text-[10px] px-1.5 py-0">Global</Badge>
                <p className="text-xs text-muted-foreground">Estimativa mensal</p>
              </div>
            </CardContent>
          </Card>
          {candidatePeriodMetrics.map(renderCard)}
        </div>
      </div>
    </div>
  );
}

